import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'motion/react';
import { MessageCircle, Send, X, Sparkles, User, PhoneCall, Loader2, SquarePen } from 'lucide-react';
import ChatMarkdown from './chat/ChatMarkdown';
import { API_ORIGIN } from '../lib/apiBase';

const LIVECHAT_API = `${API_ORIGIN}/.netlify/functions/livechat`;

type ChatMessage = {
  id: string;
  role: 'user' | 'assistant' | 'admin' | 'system';
  content: string;
  adminName?: string;
};

/** System notes the server writes for the admin log; the visitor sees their own wording instead. */
const ADMIN_ONLY_NOTES = new Set(['Customer requested to speak with a team member', 'Customer started a new chat']);
const visibleToVisitor = (m: { sender_type: string; content: string }) => !(m.sender_type === 'system' && ADMIN_ONLY_NOTES.has(m.content));

/**
 * Visitor asking for a person in their own words ("lemme speak to a human",
 * "is there anyone there"). Kept narrow: "speak to you about prices" must not
 * match, so "speak/talk to" only counts when followed by a person word.
 */
const WANTS_HUMAN =
  /\b(human|real person|actual person|live (?:person|agent)|team member|member of (?:the |your )?(?:team|staff)|customer service|operator|(?:speak|talk|chat)(?:ing)? (?:to|with) (?:a |an |the |your |some |someone |somebody )?(?:real |actual )?(?:person|people|human|team|staff|someone|somebody|agent|rep)|(?:is |are )?(?:there )?(?:any|some) ?(?:one|body) (?:there|online|available|about|real))\b/i;

/**
 * Stored messages carry a sender_type ('visitor', 'ai', 'admin', 'system'), which is
 * not the same vocabulary as the render roles. Mapping it here keeps restored and
 * polled history rendering exactly like a live reply, Markdown and avatar included.
 */
const roleFromSender = (senderType: string): ChatMessage['role'] => {
  switch (senderType) {
    case 'visitor':
      return 'user';
    case 'admin':
      return 'admin';
    case 'system':
      return 'system';
    default:
      return 'assistant';
  }
};

// Generate or retrieve visitor ID
const getVisitorId = (): string => {
  const stored = localStorage.getItem('outpost_visitor_id');
  if (stored) return stored;

  const newId = `v_${Date.now()}_${Math.random().toString(36).slice(2, 11)}`;
  localStorage.setItem('outpost_visitor_id', newId);
  return newId;
};

const UnifiedChatWidget: React.FC = () => {
  const location = useLocation();
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  // LiveChat state
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [isAdminOnline, setIsAdminOnline] = useState(false);
  const [adminJoined, setAdminJoined] = useState(false);
  const [showEscalateForm, setShowEscalateForm] = useState(false);
  const [escalateName, setEscalateName] = useState('');
  const [escalateEmail, setEscalateEmail] = useState('');
  const [escalating, setEscalating] = useState(false);
  // True once this chat has been handed to the team (button or asked in words).
  const [waitingForTeam, setWaitingForTeam] = useState(false);
  const pollInterval = useRef<NodeJS.Timeout | null>(null);
  // The session polls may still answer for after "New chat" replaced it.
  const currentSessionRef = useRef<string | null>(null);
  useEffect(() => {
    currentSessionRef.current = sessionId;
  }, [sessionId]);

  // Check admin online status
  const checkAdminStatus = useCallback(async () => {
    try {
      const response = await fetch(`${LIVECHAT_API}/status`);
      const data = await response.json();
      if (data.success) {
        setIsAdminOnline(data.isOnline);
      }
    } catch (error) {
      console.error('Failed to check admin status:', error);
    }
  }, []);

  // Initialize or restore session
  const initSession = useCallback(async () => {
    try {
      const visitorId = getVisitorId();
      const productContext = location.pathname.startsWith('/product/') ? {
        page: location.pathname,
        // Add product details if available
      } : null;

      const response = await fetch(`${LIVECHAT_API}/session`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          visitorId,
          currentPage: location.pathname,
          productContext,
        }),
      });

      const data = await response.json();
      if (data.success) {
        setSessionId(data.session.id);
        setAdminJoined(data.session.status === 'admin_joined');
        setWaitingForTeam(data.session.status === 'waiting_for_admin');

        // Restore messages from session
        if (data.session.messages && data.session.messages.length > 0) {
          const restoredMessages: ChatMessage[] = data.session.messages.filter(visibleToVisitor).map((m: any) => ({
            id: m.id,
            role: roleFromSender(m.sender_type),
            content: m.content,
            adminName: m.admin_name,
          }));
          setMessages(restoredMessages);
        }
      }
    } catch (error) {
      console.error('Failed to init session:', error);
    }
  }, [location.pathname]);

  // Poll for new messages
  const pollMessages = useCallback(async () => {
    if (!sessionId) return;

    try {
      const lastMessage = messages[messages.length - 1];
      const since = lastMessage ? new Date(Date.now() - 5000).toISOString() : undefined;

      const url = since
        ? `${LIVECHAT_API}/messages/${sessionId}?since=${since}`
        : `${LIVECHAT_API}/messages/${sessionId}`;

      const response = await fetch(url);
      const data = await response.json();
      if (currentSessionRef.current !== sessionId) return;

      if (data.success) {
        // Update admin joined status
        if (data.adminJoined && !adminJoined) {
          setAdminJoined(true);
        }

        // Add any new messages we don't have (only non-visitor messages, since visitor messages are added locally)
        if (data.messages && data.messages.length > 0) {
          // Filter to only admin/ai/system messages - we already add visitor messages locally
          const nonVisitorMessages = data.messages.filter((m: any) => m.sender_type !== 'visitor' && visibleToVisitor(m));
          const existingContents = new Set(messages.map(m => m.content));
          const newMessages = nonVisitorMessages.filter((m: any) => !existingContents.has(m.content));

          if (newMessages.length > 0) {
            const formattedNew: ChatMessage[] = newMessages.map((m: any) => ({
              id: m.id,
              role: roleFromSender(m.sender_type),
              content: m.content,
              adminName: m.admin_name,
            }));
            setMessages(prev => [...prev, ...formattedNew]);
          }
        }
      }
    } catch (error) {
      console.error('Failed to poll messages:', error);
    }
  }, [sessionId, messages, adminJoined]);

  // Auto-scroll to bottom
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, loading]);

  // Check admin status on mount and whenever the chat opens or closes
  useEffect(() => {
    checkAdminStatus();

    // Only poll periodically if chat is open
    if (isOpen) {
      const interval = setInterval(checkAdminStatus, 30000); // Check every 30s while open
      return () => clearInterval(interval);
    }
  }, [checkAdminStatus, isOpen]);

  // Initialize session when chat opens
  useEffect(() => {
    if (isOpen) {
      initSession();
    }
  }, [isOpen, initSession]);

  // Poll for messages while chat is open
  useEffect(() => {
    if (isOpen && sessionId) {
      pollInterval.current = setInterval(pollMessages, 2000); // Poll every 2s
    }

    return () => {
      if (pollInterval.current) {
        clearInterval(pollInterval.current);
      }
    };
  }, [isOpen, sessionId, pollMessages]);

  // Reset state when modal closes (livechat keeps its messages and session)
  useEffect(() => {
    if (!isOpen) {
      const timer = setTimeout(() => {
        setShowEscalateForm(false);
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  // Save message to backend
  const saveMessage = async (senderType: string, content: string) => {
    if (!sessionId) return;

    try {
      await fetch(`${LIVECHAT_API}/message`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          sessionId,
          senderType,
          content,
        }),
      });
    } catch (error) {
      console.error('Failed to save message:', error);
    }
  };

  // Escalate to human
  const handleEscalate = async () => {
    if (!sessionId) return;

    setEscalating(true);
    try {
      await fetch(`${LIVECHAT_API}/escalate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          sessionId,
          visitorName: escalateName || null,
          visitorEmail: escalateEmail || null,
        }),
      });

      setShowEscalateForm(false);
      setWaitingForTeam(true);
      setMessages(prev => [...prev, {
        id: Date.now().toString(),
        role: 'system',
        content: isAdminOnline
          ? 'A team member will join shortly. Please wait...'
          : 'Our team is currently offline. We\'ll get back to you as soon as possible!',
      }]);
    } catch (error) {
      console.error('Failed to escalate:', error);
    } finally {
      setEscalating(false);
    }
  };

  /**
   * Visitor asked for a person in their message. If the team is online right
   * now, hand the chat over (the admin gets an alert) and skip the AI reply.
   * Returns false when nobody is online, so the AI answers as before.
   */
  const handOffIfTeamOnline = async (): Promise<boolean> => {
    if (!sessionId) return false;
    let online = isAdminOnline;
    try {
      const res = await fetch(`${LIVECHAT_API}/status`);
      const data = await res.json();
      if (data.success) {
        online = !!data.isOnline;
        setIsAdminOnline(online);
      }
    } catch {
      // keep the last known status
    }
    if (!online) return false;

    await fetch(`${LIVECHAT_API}/escalate`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ sessionId, visitorName: null, visitorEmail: null }),
    });
    setWaitingForTeam(true);
    setMessages(prev => [...prev, {
      id: Date.now().toString(),
      role: 'system',
      content: 'Connecting you to a team member now. They\'ll reply here shortly.',
    }]);
    return true;
  };

  const sendMessage = useCallback(async () => {
    const trimmed = input.trim();
    if (!trimmed || loading) return;

    const userMsg: ChatMessage = {
      id: Date.now().toString(),
      role: 'user',
      content: trimmed,
    };
    setMessages(prev => [...prev, userMsg]);
    setInput('');
    setLoading(true);

    try {
      if (adminJoined) {
        // Admin has taken over - just save the message, don't call AI
        await saveMessage('visitor', trimmed);
        // The polling will pick up any admin responses
      } else {
        // LiveChat mode with AI - save message and get AI response
        await saveMessage('visitor', trimmed);

        // Asked for a person: hand over when someone is online instead of an AI reply.
        if (!waitingForTeam && WANTS_HUMAN.test(trimmed) && (await handOffIfTeamOnline())) return;

        const apiUrl = process.env.REACT_APP_CHAT_API_URL || `${API_ORIGIN}/api/chat`;
        const response = await fetch(apiUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            message: trimmed,
            history: messages.filter(m => m.role !== 'system').map(m => ({
              role: m.role === 'user' ? 'user' : 'assistant',
              content: m.content
            })),
          }),
        });

        const data = await response.json();
        const aiResponse = data.reply || data.error || 'Sorry, I had trouble with that.';

        // Save AI response to backend
        await saveMessage('ai', aiResponse);

        setMessages(prev => [...prev, {
          id: (Date.now() + 1).toString(),
          role: 'assistant',
          content: aiResponse,
        }]);

        // Nobody online: offer the leave-your-details form alongside the AI's contact info.
        if (!waitingForTeam && WANTS_HUMAN.test(trimmed)) setShowEscalateForm(true);
      }
    } catch {
      setMessages(prev => [...prev, {
        id: (Date.now() + 1).toString(),
        role: 'assistant',
        content: 'Network error. Please try again.',
      }]);
    } finally {
      setLoading(false);
    }
  }, [input, loading, messages, adminJoined, sessionId, waitingForTeam, isAdminOnline]);

  const handleKey = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const handleOpen = () => {
    setIsOpen(true);
  };

  const handleClose = () => {
    setIsOpen(false);
  };

  // Close the current session and open a fresh one for the same visitor.
  const [startingNew, setStartingNew] = useState(false);
  const startNewChat = async () => {
    setStartingNew(true);
    // Stop listening to the old session before it is closed.
    if (pollInterval.current) clearInterval(pollInterval.current);
    currentSessionRef.current = null;
    try {
      if (sessionId) {
        await fetch(`${LIVECHAT_API}/end`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ sessionId, visitorId: getVisitorId() }),
        });
      }
    } catch (error) {
      console.error('Failed to end chat:', error);
    }
    setMessages([]);
    setInput('');
    setAdminJoined(false);
    setWaitingForTeam(false);
    setShowEscalateForm(false);
    setSessionId(null);
    await initSession();
    setStartingNew(false);
  };

  return (
    <>
      <div className="fixed bottom-6 right-6 z-50" data-chat-widget>
        <AnimatePresence>
          {isOpen ? (
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              transition={{ type: 'spring', damping: 25, stiffness: 300 }}
              className="bg-[#183028] shadow-2xl flex flex-col overflow-hidden border border-white/10 fixed inset-0 sm:relative sm:inset-auto w-full h-full sm:w-[380px] sm:h-[550px] sm:rounded-2xl rounded-none"
            >
              {/* Header */}
              <div className="flex items-center justify-between px-5 py-4 border-b border-white/10 bg-gradient-to-r from-[#64a70b]/10 to-transparent">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-[#64a70b]/20 flex items-center justify-center relative">
                    <MessageCircle size={20} className="text-[#64a70b]" />
                    {adminJoined && (
                      <span className="absolute -top-1 -right-1 w-3 h-3 bg-green-500 rounded-full border-2 border-[#183028]" />
                    )}
                  </div>
                  <div>
                    <h2 className="text-lg font-semibold text-white hearns-font">
                      {adminJoined ? 'Live Chat' : 'Chat'}
                    </h2>
                    {adminJoined || isAdminOnline ? (
                      <p className="text-xs text-white/50">
                        {adminJoined ? 'Connected with team member' : 'Team available'}
                      </p>
                    ) : null}
                  </div>
                </div>
                <div className="flex items-center gap-1">
                  {messages.length > 0 ? (
                    <button
                      onClick={startNewChat}
                      disabled={startingNew || loading}
                      title="Start a new chat"
                      className="h-8 px-2.5 rounded-full flex items-center gap-1.5 text-xs font-medium text-white/70 hover:text-white hover:bg-white/10 transition-colors disabled:opacity-50"
                    >
                      {startingNew ? <Loader2 size={15} className="animate-spin" /> : <SquarePen size={15} />}
                      New chat
                    </button>
                  ) : null}
                  <button
                    onClick={handleClose}
                    aria-label="Close chat"
                    className="w-8 h-8 rounded-full flex items-center justify-center hover:bg-white/10 transition-colors"
                  >
                    <X size={20} className="text-white/60" />
                  </button>
                </div>
              </div>

              {/* Content */}
              <div className="flex-1 flex flex-col sm:flex-row overflow-hidden">
                {/* Chat Panel */}
                <div className="flex flex-col w-full">
                  {/* Messages */}
                  <div className="flex-1 overflow-y-auto p-4 space-y-4">
                    {messages.length === 0 && (
                      <div className="text-center py-8">
                        <div className="w-14 h-14 rounded-full bg-[#64a70b]/15 flex items-center justify-center mx-auto mb-4">
                          <Sparkles size={28} className="text-[#64a70b]" />
                        </div>
                        <h3 className="text-base font-medium text-white mb-2 hearns-font">
                          Hi there!
                        </h3>
                        <p className="text-sm text-white/50 max-w-xs mx-auto">
                          Ask about our services, pricing, or anything else
                        </p>
                      </div>
                    )}

                    {messages.map((msg) => (
                      <div
                        key={msg.id}
                        className={`flex gap-2 ${msg.role === 'user' ? 'justify-end' : msg.role === 'system' ? 'justify-center' : 'justify-start'}`}
                      >
                        {msg.role === 'system' ? (
                          <div className="px-4 py-2 rounded-xl bg-yellow-500/10 text-yellow-300/80 text-sm text-center max-w-[90%]">
                            {msg.content}
                          </div>
                        ) : (
                          <>
                            {(msg.role === 'assistant' || msg.role === 'admin') && (
                              <div className={`w-7 h-7 rounded-full flex-shrink-0 flex items-center justify-center ${
                                msg.role === 'admin' ? 'bg-purple-500/20' : 'bg-[#64a70b]/20'
                              }`}>
                                {msg.role === 'admin' ? (
                                  <User size={14} className="text-purple-400" />
                                ) : (
                                  <Sparkles size={14} className="text-[#64a70b]" />
                                )}
                              </div>
                            )}
                            <div className="flex flex-col max-w-[80%]">
                              {msg.role === 'admin' && msg.adminName && (
                                <span className="text-xs text-purple-400 mb-1">{msg.adminName}</span>
                              )}
                              <div
                                className={`px-3.5 py-2.5 rounded-2xl text-[13.5px] ${
                                  msg.role === 'user'
                                    ? 'bg-[#64a70b] text-white rounded-br-sm'
                                    : msg.role === 'admin'
                                      ? 'bg-purple-500/20 text-purple-100 rounded-bl-sm'
                                      : 'bg-white/10 text-white/90 rounded-bl-sm'
                                }`}
                              >
                                {msg.role === 'assistant'
                                  ? <ChatMarkdown text={msg.content} />
                                  : msg.content}
                              </div>
                            </div>
                            {msg.role === 'user' && (
                              <div className="w-7 h-7 rounded-full bg-white/10 flex-shrink-0 flex items-center justify-center">
                                <User size={14} className="text-white/70" />
                              </div>
                            )}
                          </>
                        )}
                      </div>
                    ))}

                    {loading && (
                      <div className="flex gap-2">
                        <div className="w-7 h-7 rounded-full bg-[#64a70b]/20 flex-shrink-0 flex items-center justify-center">
                          <Sparkles size={14} className="text-[#64a70b]" />
                        </div>
                        <div className="bg-white/10 px-4 py-3 rounded-2xl rounded-bl-sm">
                          <div className="flex gap-1">
                            {[0, 1, 2].map((i) => (
                              <motion.span
                                key={i}
                                className="w-2 h-2 rounded-full bg-[#64a70b]"
                                animate={{ y: [0, -4, 0] }}
                                transition={{ duration: 0.6, repeat: Infinity, delay: i * 0.15 }}
                              />
                            ))}
                          </div>
                        </div>
                      </div>
                    )}

                    <div ref={messagesEndRef} />
                  </div>

                  {/* Escalate Form */}
                  {showEscalateForm && !adminJoined && (
                    <div className="p-4 border-t border-white/10 bg-white/5">
                      <p className="text-sm text-white/70 mb-3">
                        {isAdminOnline
                          ? 'Enter your details and a team member will join shortly:'
                          : 'Leave your details and we\'ll get back to you:'}
                      </p>
                      <div className="space-y-2">
                        <input
                          type="text"
                          value={escalateName}
                          onChange={(e) => setEscalateName(e.target.value)}
                          placeholder="Your name (optional)"
                          className="w-full px-3 py-2 rounded-lg bg-white/10 text-white text-base placeholder-white/40 outline-none border border-white/10 focus:border-[#64a70b]/50"
                          style={{ fontSize: '16px' }}
                        />
                        <input
                          type="email"
                          value={escalateEmail}
                          onChange={(e) => setEscalateEmail(e.target.value)}
                          placeholder="Your email (optional)"
                          className="w-full px-3 py-2 rounded-lg bg-white/10 text-white text-base placeholder-white/40 outline-none border border-white/10 focus:border-[#64a70b]/50"
                          style={{ fontSize: '16px' }}
                        />
                        <div className="flex gap-2">
                          <button
                            onClick={() => setShowEscalateForm(false)}
                            className="flex-1 px-3 py-2 rounded-lg text-sm text-white/60 hover:bg-white/5"
                          >
                            Cancel
                          </button>
                          <button
                            onClick={handleEscalate}
                            disabled={escalating}
                            className="flex-1 px-3 py-2 rounded-lg bg-[#64a70b] text-white text-sm font-medium disabled:opacity-50"
                          >
                            {escalating ? <Loader2 className="w-4 h-4 animate-spin mx-auto" /> : 'Connect'}
                          </button>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Input */}
                  <div className="p-3 border-t border-white/10">
                    {/* Talk to human button (not when admin joined) */}
                    {!adminJoined && !waitingForTeam && !showEscalateForm && messages.length >= 2 && (
                      <button
                        onClick={() => setShowEscalateForm(true)}
                        className="w-full mb-2 px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-sm text-white/70 hover:bg-white/10 hover:text-white transition-colors flex items-center justify-center gap-2"
                      >
                        <PhoneCall size={14} />
                        <span>Speak to a team member</span>
                        {isAdminOnline && (
                          <span className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
                        )}
                      </button>
                    )}

                    <div className="flex items-center gap-2 px-3 py-2 rounded-full bg-white/5 border border-white/10">
                      <input
                        type="text"
                        value={input}
                        onChange={(e) => setInput(e.target.value)}
                        onKeyDown={handleKey}
                        placeholder="Type a message..."
                        disabled={loading}
                        className="flex-1 bg-transparent text-base sm:text-sm text-white placeholder-white/40 outline-none"
                        style={{ fontSize: '16px' }}
                      />
                      <button
                        onClick={sendMessage}
                        disabled={!input.trim() || loading}
                        className="w-8 h-8 rounded-full flex items-center justify-center bg-[#64a70b] disabled:opacity-40 transition-opacity"
                      >
                        <Send size={16} className="text-white" />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          ) : (
            <>
              {/* Main toggle button */}
              <motion.button
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={handleOpen}
                className="flex items-center gap-2 sm:gap-3 px-3 sm:px-5 py-2.5 sm:py-3 rounded-full bg-[#64a70b] text-white font-semibold shadow-lg shadow-[#64a70b]/30"
              >
                <MessageCircle size={20} />
                <span className="hidden sm:inline">Chat with us</span>
              </motion.button>
            </>
          )}
        </AnimatePresence>
      </div>
    </>
  );
};

export default UnifiedChatWidget;
